import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_HIGH,
  KEY_BACKSPACE_COMMAND,
  KEY_DOWN_COMMAND,
  FORMAT_TEXT_COMMAND,
  $isParagraphNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isElementNode,
  LexicalNode,
} from 'lexical';
import { $isQuoteNode } from '@lexical/rich-text';
import { 
  $createWhatsAppCodeBlockNode, 
  $createWhatsAppQuoteNode, 
  $isWhatsAppCodeBlockNode,
  $isWhatsAppQuoteNode
} from '../nodes';

/**
 * Plugin to handle keyboard behavior fixes and shortcuts
 * 
 * Fixes:
 * 1. Backspace at the beginning of a quote block should remove empty quote blocks
 * 2. Shortcuts for formatting (Strikethrough, Code, Code Block, Quote)
 */
export function KeyboardBehaviorPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Shortcuts Listener
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        const { ctrlKey, metaKey, shiftKey, key } = event;
        const isMod = ctrlKey || metaKey;

        if (!isMod) return false;

        // Strikethrough: Ctrl+Shift+S
        if (shiftKey && (key.toLowerCase() === 's')) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
          return true;
        }

        // Inline Code: Ctrl+Shift+C
        if (shiftKey && (key.toLowerCase() === 'c')) {
          event.preventDefault();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
          return true;
        }

        // Code Block: Ctrl+Shift+M
        if (shiftKey && (key.toLowerCase() === 'm')) {
           event.preventDefault();
           editor.update(() => {
             const selection = $getSelection();
             if ($isRangeSelection(selection)) {
                const nodes = selection.getNodes();
                const topLevelElements = new Set<any>();
                
                nodes.forEach((node) => {
                  const top = node.getTopLevelElement();
                  if (top) topLevelElements.add(top);
                });
                
                if (topLevelElements.size === 0) {
                     const anchor = selection.anchor.getNode();
                     const top = anchor.getTopLevelElement();
                     if (top) topLevelElements.add(top);
                }
                
                const allAreCodeBlocks = Array.from(topLevelElements).every(node => $isWhatsAppCodeBlockNode(node));
                
                if (allAreCodeBlocks) {
                    if (selection.isCollapsed()) {
                         const anchorElement = selection.anchor.getNode().getTopLevelElement();
                         if (anchorElement) {
                             const paragraph = $createParagraphNode();
                             anchorElement.insertAfter(paragraph);
                             paragraph.selectStart();
                         }
                    } else {
                         topLevelElements.forEach(node => {
                            if ($isWhatsAppCodeBlockNode(node)) {
                                 const paragraph = $createParagraphNode();
                                 const children = node.getChildren();
                                 children.forEach(child => paragraph.append(child));
                                 node.replace(paragraph);
                            }
                         });
                    }
                } else {
                    topLevelElements.forEach(node => {
                        if (!$isWhatsAppCodeBlockNode(node) && $isElementNode(node)) {
                            const codeBlock = $createWhatsAppCodeBlockNode();
                            const children = node.getChildren();
                            children.forEach(child => codeBlock.append(child));
                            node.replace(codeBlock);
                        }
                    });
                }
             }
           });
           return true;
        }

        // Quote: Ctrl+Shift+> (or . since > is shift+.)
        if (shiftKey && (key === '>' || key === '.')) {
            event.preventDefault();
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                // Gather Top Level Elements
                const nodes = selection.getNodes();
                const topLevelElements = new Set<any>();
                nodes.forEach((node) => {
                    const top = node.getTopLevelElement();
                    if (top) topLevelElements.add(top);
                });
                
                if (topLevelElements.size === 0) {
                     const anchor = selection.anchor.getNode();
                     const top = anchor.getTopLevelElement();
                     if (top) topLevelElements.add(top);
                }

                // Check if all are quoted
                const allAreQuoted = Array.from(topLevelElements).every(node => $isWhatsAppQuoteNode(node) || $isQuoteNode(node));

                if (allAreQuoted) {
                    if (selection.isCollapsed()) {
                         // Exit Quote (create new para after)
                         const anchorElement = selection.anchor.getNode().getTopLevelElement();
                         if (anchorElement) {
                             const paragraph = $createParagraphNode();
                             anchorElement.insertAfter(paragraph);
                             paragraph.selectStart();
                         }
                    } else {
                        // Unwrap (Toggle Off)
                        topLevelElements.forEach(node => {
                            if ($isWhatsAppQuoteNode(node) || $isQuoteNode(node)) {
                                const children = node.getChildren();
                                let lastInserted: LexicalNode = node;
                                children.forEach(child => {
                                    lastInserted.insertAfter(child);
                                    lastInserted = child;
                                });
                                node.remove();
                            }
                        });
                    }
                } else {
                    // Wrap in Quote
                     topLevelElements.forEach(node => {
                        if (!$isWhatsAppQuoteNode(node) && !$isQuoteNode(node) && $isElementNode(node)) {
                             const quoteNode = $createWhatsAppQuoteNode();
                             const quoteParagraph = $createParagraphNode();
                             const children = node.getChildren();
                             children.forEach(child => quoteParagraph.append(child));
                             quoteNode.append(quoteParagraph);
                             node.replace(quoteNode);
                        }
                    });
                }
              }
            });
            return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  useEffect(() => {
    // Handle backspace to remove empty quote blocks
    const removeBackspaceListener = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      (event: KeyboardEvent) => {
        const selection = $getSelection();
        
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false; // Let default behavior handle non-collapsed selections
        }

        const anchorNode = selection.anchor.getNode();
        const anchorOffset = selection.anchor.offset;

        // Check if we're at the beginning of a text node (offset 0)
        if (anchorOffset !== 0) {
          return false; // Not at the beginning, use default behavior
        }

        // Get the parent paragraph
        let parent = anchorNode.getParent();
        
        // If the anchor node is a paragraph, use it directly
        if ($isParagraphNode(anchorNode)) {
          parent = anchorNode;
        }

        // Check if the paragraph is inside a quote
        if (parent && $isParagraphNode(parent)) {
          const quoteParent = parent.getParent();
          
          if (quoteParent && ($isQuoteNode(quoteParent) || $isWhatsAppQuoteNode(quoteParent))) {
            // We're at the beginning of a quote block
            // After backspace, check if the quote becomes empty and remove it
            
            // Use setTimeout to check after the default backspace behavior
            setTimeout(() => {
              editor.update(() => {
                // Re-get the quote node (it might have changed)
                const currentSelection = $getSelection();
                if (!$isRangeSelection(currentSelection)) return;
                
                const currentNode = currentSelection.anchor.getNode();
                let currentParent = currentNode.getParent();
                
                if ($isParagraphNode(currentNode)) {
                  currentParent = currentNode;
                }
                
                // Check if we're still in a quote or if there's a quote sibling
                const root = editor.getEditorState()._nodeMap;
                root.forEach((node: any) => {
                  if ($isQuoteNode(node) || $isWhatsAppQuoteNode(node)) {
                    // Check if this quote is empty
                    const children = node.getChildren();
                    
                    if (children.length === 0) {
                      // Quote is completely empty, remove it
                      node.remove();
                    } else if (children.length === 1) {
                      const child = children[0];
                      if ($isParagraphNode(child)) {
                        const textContent = child.getTextContent();
                        if (textContent === '') {
                          // Quote has only an empty paragraph, remove it
                          node.remove();
                        }
                      }
                    }
                  }
                });
              });
            }, 0);
          }
        }

        return false; // Let default backspace behavior proceed
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      removeBackspaceListener();
    };
  }, [editor]);

  return null;
}
